import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router";
import { Card, Button } from "components/ui";
import { Page } from "components/shared/Page";
import "styles/mobile-responsive.css";
import { transformLeadDetailsApiData } from "utils/leadDataTransforms";
import { LeadOptimisticStateProvider, useLeadOptimisticState } from "contexts/LeadOptimisticStateContext";

// Constants and Services
import { getLead, bulkUpdateLeadStage, getLeadJourneyIndicators } from "services/leadsService";
import { ACTIVITY_STATUS } from "constants/activityStatus.constant";
import LeadRightDrawerContent from "components/leads/LeadDetails/LeadRightDrawerContent";
import SidebarNavigation from "components/leads/LeadDetails/SidebarNavigation";
import TimelineView from "components/leads/LeadDetails/TimeLineView";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { useConfirmModal } from "hooks/useConfirmModal";
import { invalidateTimelineCache } from "hooks/useTimeline";
import { invalidateNotesCache } from "hooks/useNotes";
import QuickLeadModal from "components/leads/LeadActions/QuickLeadModal";
import NotesTab from "components/leads/LeadDetails/NotesTab";
import ClientNotesTab from "components/shared/ClientNotes/ClientNotesTab";
import CalendarTab from "components/leads/LeadDetails/CalendarTab";
import CommunicationsTab from "components/leads/LeadDetails/CommunicationsTab";
import DocumentsTab from "components/leads/LeadDetails/DocumentsTab/DocumentsTab";
import { useDocuments } from "components/leads/LeadDetails/DocumentsTab/hooks/useDocuments";
import OfferLetterTab from "components/leads/LeadDetails/OfferLetterTab";
import LeadManagementTab from "components/leads/LeadDetails/LeadManagementTab";
import { toast } from "sonner";
import StageChangeModal from "components/leads/LeadActions/StageChangeModal";
import { shouldTriggerJobConversion } from "services/leadStageService";
import { shouldWarnJobCancellation, shouldOfferJobReactivation } from "utils/leadStageHelpers";
import { ConvertToJobModal } from "app/pages/jobs/components/ConvertToJobModal";
import { SetJobStatusModal } from "app/pages/jobs/components/SetJobStatusModal";
import { getJobByLeadId } from "services/jobsService";
import AssignAcademicCounsellorModal from "components/leads/LeadActions/AssignAcademicCounsellorModal";
import AssignAccountantCounsellorModal from "components/leads/LeadActions/AssignAccountantCounsellorModal";

// Contacts
import ContactsTab from "components/leads/LeadDetails/ContactsTab";



// Modal Components
import AssignOwnerModal from "components/leads/LeadActions/AssignOwnerModal";
import SetPriorityModal from "components/leads/LeadActions/SetPriorityModal";
import LeadAnalyticsModal from "components/leads/LeadActions/LeadAnalyticsModal";
import WhatsAppMessageModal from "components/leads/LeadActions/WhatsAppMessageModal";
import EmailMessageModal from "components/leads/LeadActions/EmailMessageModal";
import SMSMessageModal from "components/leads/LeadActions/SMSMessageModal";
import FollowUpScheduleModal from "components/leads/LeadActions/FollowUpScheduleModal";
import CallModal from "components/leads/LeadActions/CallModal";
import { useFollowUpActions, useFollowUpEvents } from "hooks/useFollowUp";
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  UserIcon,
  ClockIcon,
  FlagIcon,
  ChatBubbleLeftEllipsisIcon,
  Cog6ToothIcon,
  // PhoneIcon,
  // EnvelopeIcon,
  // CalenderDaysIcon,
  // DevicePhoneMobileIcon,
  // ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Select, Textarea } from "components/ui/Form";
import { Badge } from "components/ui";
import SetInteractionLevelModal from "components/leads/LeadActions/SetInteractionLevelModal";
import { canViewLeadDetails, canEditLead, canAssignLead } from "permissions/permissionHelpers";
const formatTimeLocal = (timeStr) => {
  if (!timeStr) return "";
  if (/AM|PM/i.test(timeStr)) {
    return timeStr;
  }
  const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  }
  return timeStr;
};

const LeadDetailsInner = ({ leadIdProp = null, leadDataProp = null, isModal = false, onClose = null, onFullClose = null, entityName = null, onLeadUpdate = null, onDataLoaded = null, initialSelectedTab = "timeline", initialSubTab = 0, scrollToSection = null, openNotesAddModal = false, setOpenNotesAddModal = null }) => {

  const contentRef = useRef(null);
  const finalEntityName = entityName || "Lead"; // Default to Lead if not provided
  const isInitialLoad = useRef(true);
  const optimisticDataRef = useRef({});
  const params = useParams();

  const leadId = leadIdProp || params.id;
  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
  const [selectedContentTab, setSelectedContentTab] = useState("payment-details");
  const [refreshTrigger, setRefreshTrigger] = useState(0);


  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(!leadDataProp);
  const [error, setError] = useState(null);
  const [showRightPanel, setShowRightPanel] = useState(false);
  // const [rawLeadData, setLeadData] = useState(leadDataProp || null);

  const {
    leadData,
    setLeadData,
    localStage,
    localSubStage,
    localPriority,
    localInteractionLevel,
    setLocalStage,
    setLocalSubStage,
    setLocalPriority,
    setLocalInteractionLevel
  } = useLeadOptimisticState();
  const [apiResponse, setApiResponse] = useState(null);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFlaged, setIsFlaged] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [progressData, setProgressData] = useState({});
  const [showQuickLeadModal, setShowQuickLeadModal] = useState(false);
  const [selectedFormForApplication, setSelectedFormForApplication] = useState(null);
  const [isComingFromPaymentFlow, setIsComingFromPaymentFlow] = useState(false);

  const useDocumentsData = useDocuments(leadId);
  const { totalRecords: apiDocumentsCount } = useDocumentsData;

  const documentsCount = apiDocumentsCount || 0;



  const stageConstant = leadData?.StageConstant || "N/A";
  const isReadOnly = finalEntityName === "Applicant" ||
    finalEntityName === "Application" ||
    stageConstant?.toLowerCase() === "applicant" ||
    stageConstant?.toLowerCase() === "application" ||
    stageConstant?.toLowerCase() === "win" ||
    stageConstant?.toLowerCase() === "closed" ||
    leadData?.IsStudent;

  const [visitedTabs, setVisitedTabs] = useState(new Set([initialSelectedTab]));

  useEffect(() => {
    if (selectedTab) {
      setVisitedTabs(prev => new Set([...prev, selectedTab]));
    }
  }, [selectedTab]);

  // Stage change confirmation modal
  const [stageConfirmModal, setStageConfirmModal] = useState({
    show: false,
    state: "pending",
    loading: false,
  });
  const [stageChangeData, setStageChangeData] = useState(null);

  // Modal states for all action modals
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showInteractionLevelModal, setShowInteractionLevelModal] =
    useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showAcademicModal, setShowAcademicModal] = useState(false);
  const [showAccountantModal, setShowAccountantModal] = useState(false);
  const [showConvertToJobModal, setShowConvertToJobModal] = useState(false);
  const [showReactivateJobModal, setShowReactivateJobModal] = useState(false);
  const [reactivateJobId, setReactivateJobId] = useState(null);
  const [reactivateCurrentStatusId, setReactivateCurrentStatusId] = useState(null);
  const [pendingRevertLeadStageData, setPendingRevertLeadStageData] = useState(null);
  const [isRevertingStage, setIsRevertingStage] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [selectedFollowUpForStatus, setSelectedFollowUpForStatus] = useState(null);
  const [statusFormData, setStatusFormData] = useState({
    status: '',
    statusId: null,
    outcome: '',
    outcomeId: null,
    eventTypeId: null,
    remarks: ''
  });
  // ── Follow-up hooks ──────────────────────────────────────────────
  const { events: followUpEvents, isLoading: loadingEvents } = useFollowUpEvents();
  const followUpActions = useFollowUpActions({ leadId });
  // Internal state for notes modal (works in both page and modal mode)
  const [internalOpenNotesAddModal, setInternalOpenNotesAddModal] = useState(false);


  // Status options (using central ACTIVITY_STATUS constant)
  const statusOptions = ACTIVITY_STATUS;

  // Final statuses that cannot be updated further
  const FINAL_STATUS_IDS = [2, 3, 4, 5]; // Completed, Missed, Canceled/Cancelled, ReScheduled/Failed
  const FINAL_STATUS_NAMES = ['Completed', 'Missed', 'Canceled', 'Cancelled', 'ReScheduled', 'Rescheduled', 'Failed'];

  // Helper to check if a follow-up status is final
  const isStatusFinal = (followUp) => {
    if (!followUp) return false;
    const statusId = followUp.StatusId || followUp.statusId;
    const statusName = followUp.StatusName || followUp.status;
    return FINAL_STATUS_IDS.includes(statusId) || FINAL_STATUS_NAMES.includes(statusName);
  };
  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle external note add modal trigger - switch to notes tab
  useEffect(() => {
    if (openNotesAddModal) {
      setSelectedTab('notes');
    }
  }, [openNotesAddModal]);

  // followUpEvents & loadingEvents are now provided by useFollowUpEvents() hook above

  // Fetch lead data function - extracted for reuse
  const fetchLeadData = async (silent = false) => {
    // Handle optimistic updates if an object is passed
    if (typeof silent === 'object' && silent !== null) {
      setLeadData(prev => ({
        ...prev,
        ...silent
      }));
    }

    if (!leadId) {
      setError("No lead ID provided");
      setLoading(false);
      return;
    }

    try {
      if (!silent || typeof silent === 'object') setLoading(true);
      setError(null);

      if (silent === true || (silent && typeof silent === 'object')) {
        if (typeof silent === 'object') setLoading(false);
        // Give the backend a moment to update before we refetch
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const result = await getLead(leadId, true);
      if (result.success && result.data.data) {
        setApiResponse(result.data.data);

        // Transform API response to component-friendly format
        const transformedData = transformLeadDetailsApiData(
          result.data.data,
          leadId,
        );
        // Merge with optimistic data if present to prevent stale API data from reverting the UI
        setLeadData(prev => ({
          ...transformedData,
          ...optimisticDataRef.current,
          ...(typeof silent === 'object' ? silent : {})
        }));
        if (onDataLoaded) onDataLoaded(transformedData);
      } else {
        // Fallback to dummy data if API fails
        console.warn("API failed, using dummy data:", result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error("Error fetching lead data:", err);
      setError(err.message);
      // Fallback to dummy data
    } finally {
      setLoading(false);
      // Notify parent about the update if this is a refresh (not initial load)
      if (!isInitialLoad.current && onLeadUpdate) {
        onLeadUpdate(leadId, 'update');
      }
      isInitialLoad.current = false;
    }
  };

  // Fetch lead data when component mounts or leadId changes
  useEffect(() => {
    // ✅ Reset optimistic state and clear stale data when switching leads
    setLocalStage(null);
    setLocalSubStage(null);
    setLocalPriority(null);
    setLocalInteractionLevel(null);

    // ✅ Skip fetch if we already have the data (from drawer container)
    // Use loose equality or cast to handle potential String/Number mismatches
    const matchesId = leadDataProp && (
      String(leadDataProp.id || leadDataProp.LeadId) === String(leadId) ||
      String(leadDataProp.leadId) === String(leadId)
    );

    if (matchesId) {
      setLeadData({
        ...leadDataProp,
      });
      setLoading(false);
      return;
    }

    // Force clear old data so we don't see "ghost" data while loading
    setLeadData(null);

    // Only fetch if we don't have matching data
    if (leadId) {
      fetchLeadData();
    }
  }, [leadId]);

  const lastInitialTabRef = useRef(initialSelectedTab);

  // Update selected tab when initialSelectedTab changes (for deep linking)
  useEffect(() => {
    // Only update if the initialSelectedTab prop actually CHANGED from its previous value
    if (initialSelectedTab && initialSelectedTab !== lastInitialTabRef.current) {
      setSelectedTab(initialSelectedTab);
      lastInitialTabRef.current = initialSelectedTab;
    }
  }, [initialSelectedTab]);

  // Auto-scroll to section and set correct sub-tab when scrollToSection is provided
  useEffect(() => {
    if (scrollToSection && leadData) {
      // If the scrollToSection is a sub-tab of the leads profile
      const isSubTab = [
        "lead-details",
        "offer-letter",
        "follow-up-calendar",
        "application-form",
        "google-meet",
      ].includes(scrollToSection);

      if (isSubTab) {
        setSelectedTab("leads-tab");
        setSelectedContentTab(scrollToSection);
      } else {
        // Handle main tabs
        const mainTabMapping = {
          'notes': 'notes',
          'documents': 'documents',
          'timeline': 'timeline',
          'communications': 'communications',
          'tickets': 'tickets'
        };
        if (mainTabMapping[scrollToSection]) {
          setSelectedTab(mainTabMapping[scrollToSection]);
        }
      }

      // Minimal delay (50ms) to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const element = document.getElementById(`section-${scrollToSection}`);
        if (element) {
          element.scrollIntoView({
            behavior: "auto",
            block: "start",
            inline: "nearest",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [scrollToSection]);

  const handleRefreshLead = async () => {
    setRefreshTrigger((prev) => prev + 1);
    invalidateTimelineCache(leadId);
    await fetchLeadData(true);  // this already reloads the lead API
    if (onLeadUpdate) onLeadUpdate();
  };


  const handleSave = async () => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsEditing(false);
    setLoading(false);
  };
  const handleCancelReactivate = async () => {
    if (pendingRevertLeadStageData) {
      const { leadId, stageId, subStageId } = pendingRevertLeadStageData;
      if (leadId && stageId) {
        setIsRevertingStage(true);
        try {
          const revertRes = await bulkUpdateLeadStage({
            leadIds: [leadId],
            stageId: stageId,
            subStageId: subStageId,
            remarks: "Stage change reverted due to job reactivation cancellation",
          });
          if (revertRes.success) {
            toast.success("Lead restored to its previous stage successfully");
            setPendingRevertLeadStageData(null);
            setShowReactivateJobModal(false);
            fetchLeadData(true);
          } else {
            toast.error(revertRes.error || "Failed to revert Lead stage");
          }
        } catch (err) {
          console.error("Error reverting lead stage:", err);
          toast.error("An error occurred while reverting Lead stage");
        } finally {
          setIsRevertingStage(false);
          setShowCancelReactivateConfirm(false);
        }
      } else {
        setPendingRevertLeadStageData(null);
        setShowReactivateJobModal(false);
        setShowCancelReactivateConfirm(false);
      }
    } else {
      setShowReactivateJobModal(false);
      setShowCancelReactivateConfirm(false);
    }
  };

  // Standardized stage change confirmation
  const stageChangeConfirm = useConfirmModal({
    type: "stage-change",
    autoCloseSuccess: 1500,
    onConfirm: async (stageData) => {
      const prevStageId = leadData?.StageId || leadData?.stageId || null;
      const prevSubStageId = leadData?.SubStageId || leadData?.subStageId || null;
      const stageName = stageData.leadStage;
      const subStageName = stageData.subStage;
      const stageConstant = stageData.stageConstant || stageData.StageConstant;
      const subStageConstant = stageData.subStageConstant || stageData.SubStageConstant;

      // ✅ Optimistic ref update
      optimisticDataRef.current = {
        ...optimisticDataRef.current,
        stage: stageName,
        subStage: subStageName
      };

      // ✅ Single state update (FIXED)
      setLeadData(prev => ({
        ...prev,
        stage: stageName,
        subStage: subStageName,
        StageName: stageName,
        SubStageName: subStageName
      }));

      // Local UI state
      setLocalStage(stageName);
      setLocalSubStage(subStageName);

      // API call
      const apiPayload = {
        leadIds: [stageData.leadId],
        stageId: stageData.stageId,
        subStageId: stageData.subStageId,
        remarks: stageData.notes || "",
        leadScore: stageData.leadScore,
        fieldValues: stageData.fieldValues,
      };
      
      const result = await bulkUpdateLeadStage(apiPayload);
      

      if (result.success) {
        // Show Convert to Job modal when target stage is eligible and lead is not converted
        if (shouldTriggerJobConversion(stageData, leadData)) {
          setTimeout(() => setShowConvertToJobModal(true), 1800);
        }

        // Offer to reactivate cancelled job if lead is already converted and target stage is a job stage
        if (shouldOfferJobReactivation(stageData, leadData)) {
          const leadId = stageData.leadId;
          getJobByLeadId(leadId).then((res) => {
            if (res.success && res.data) {
              let jobData = res.data?.data || res.data;
              if (typeof jobData === "string") {
                try { jobData = JSON.parse(jobData); } catch {}
              }
              if (Array.isArray(jobData) && jobData.length > 0) jobData = jobData[0];
              const jobObj = jobData?.Job || jobData;
              const statusName = (jobObj?.StatusName || "").toLowerCase();
              const statusId = jobObj?.StatusId;
              const jobId = jobObj?.JobId;
                if (jobId && statusName === "cancelled") {
                  setReactivateJobId(jobId);
                  setReactivateCurrentStatusId(statusId);
                  setPendingRevertLeadStageData({
                    leadId,
                    stageId: prevStageId,
                    subStageId: prevSubStageId,
                  });
                  setTimeout(() => setShowReactivateJobModal(true), 1800);
                }
            }
          });
        }

        // ✅ Handle formId response
        if (result.data) {
          try {
            const parsedData =
              typeof result.data === "string"
                ? JSON.parse(result.data)
                : result.data;

            if (parsedData.formId) {
              setSelectedFormForApplication({ FormId: parsedData.formId });

              setTimeout(() => {
                setShowQuickLeadModal(true);
              }, 500);
            }
          } catch (error) {
            console.error("Error parsing formId from response:", error);
          }
        }

        // ✅ Refetch latest data
        await new Promise(resolve => setTimeout(resolve, 500));

        const refreshResult = await getLead(stageData.leadId);
        if (refreshResult.success && refreshResult.data.data) {
          const transformedData = transformLeadDetailsApiData(
            refreshResult.data.data,
            stageData.leadId
          );

          setLeadData(transformedData);
          optimisticDataRef.current = {};
        }

        if (onLeadUpdate) {
          onLeadUpdate(stageData.leadId, "update");
        }
        invalidateTimelineCache(stageData.leadId);
        invalidateNotesCache(stageData.leadId);
        setRefreshTrigger((prev) => prev + 1);
      }

      return result;
    },

    messages: {
      pending: {
        title: "Change Lead Stage?",
        description: `Are you sure you want to change the stage for ${leadData?.name || "this lead"
          }?`,
        actionText: "Change Stage",
      },
      success: {
        title: "Stage Changed Successfully",
        description: `Successfully updated the stage for ${leadData?.name || "the lead"
          }.`,
      },
    },
  });

  const handleStageChange = async (stageData, resetFormData = null) => {
    // Check if stageData is valid
    if (!stageData?.leadId || !stageData.stageId) {
      console.error("Missing required stage change data");
      toast.error("Error: Stage ID is missing. Please select a valid stage.");
      return;
    }

    try {
      setStageChangeData(stageData);

      // Call the form reset function if provided (from modal/panel)
      if (resetFormData && typeof resetFormData === "function") {
        resetFormData();
      }

      // Close any open modals/panels
      setShowStageModal(false);

      // Swap in the job-cancellation warning copy when this lead is already
      // converted and the target stage would take it off the job's 3 stages.
      stageChangeConfirm.updateMessages({
        pending: shouldWarnJobCancellation(stageData, leadData)
          ? {
              title: "Change Lead Stage & Cancel Job?",
              description: "This lead is converted to a job. Changing to this stage will update the associated job's status to Cancelled. Are you sure you want to continue?",
              actionText: "Yes, Proceed",
            }
          : {
              title: "Change Lead Stage?",
              description: `Are you sure you want to change the stage for ${leadData?.name || "this lead"}?`,
              actionText: "Change Stage",
            },
      });

      // Open confirmation modal
      stageChangeConfirm.openModal(stageData);
    } catch (error) {
      console.error("Error in handleStageChange:", error);
      toast.error("An error occurred while processing the stage change.");
    }
  };

  const handleOwnerChange = async () => {
    setRefreshTrigger((prev) => prev + 1);
    invalidateTimelineCache(leadId);
    await fetchLeadData(true);
  };




  const handleEditFollowUp = (followUp) => {
    // Transform the follow-up data to the format expected by the modal
    const editData = {
      id: followUp.FollowUpId || followUp.id,
      leadId: leadData?.id || followUp.LeadId || followUp.leadId,
      userId: followUp.UserId || followUp.userId,
      userName: followUp.LeadName || followUp.userName || leadData?.name,
      status: followUp.StatusName || followUp.status,
      statusId: followUp.StatusId || followUp.statusId,
      outcomeId: followUp.OutcomeId || followUp.outcomeId,
      remarks: followUp.Remarks || followUp.remarks,
      notes: followUp.Notes || followUp.notes, // Missing field
      Notes: followUp.Notes || followUp.notes, // Missing field (PascalCase)
      scheduledTime: followUp.scheduledTime || followUp.ScheduledTime,
      date: followUp.ScheduledDate || followUp.date,
      priority: followUp.Priority || followUp.priority,
      // Use AssignedToId (GUID) instead of AssignedTo (name)
      assignedTo: followUp.AssignedToId || followUp.assignedToId || followUp.AssignedTo || followUp.assignedTo,
      assignedToName: followUp.AssignedTo || followUp.AssignedToName || followUp.assignedToName,
      channelId: followUp.ChannelId || followUp.channelId,
      type: followUp.type
    };

    setEditingFollowUp(editData);
    setShowFollowUpModal(true);
  };

  const handleUpdateFollowUpStatus = (followUp) => {
    // Check if status is already final
    if (isStatusFinal(followUp)) {
      toast.error('Cannot update status', {
        description: `This follow-up is already marked as ${followUp.StatusName || followUp.status}`,
        duration: 3000,
      });
      return;
    }

    // Set the selected follow-up with proper field mapping
    const followUpData = {
      ...followUp,
      // Map AssignedToId to assignedTo for consistency
      assignedTo: followUp.AssignedToId || followUp.assignedToId || followUp.AssignedTo || followUp.assignedTo,
      assignedToName: followUp.AssignedTo || followUp.AssignedToName || followUp.assignedToName
    };

    setSelectedFollowUpForStatus(followUpData);

    // Match the existing status by name to get the correct local statusOption id
    const currentStatusName = followUp.StatusName || followUp.status || '';
    const matchedStatus = statusOptions.find(
      s => s.name.toLowerCase() === currentStatusName.toLowerCase()
    );

    // Pre-fill the status form
    setStatusFormData({
      status: matchedStatus?.name || currentStatusName || '',
      statusId: matchedStatus?.id || null,
      outcome: followUp.EventName || followUp.eventName || '',
      outcomeId: followUp.OutcomeId || followUp.outcomeId || null,
      eventTypeId: followUp.EventTypeId || followUp.eventTypeId || followUp.OutcomeId || followUp.outcomeId || null,
      remarks: followUp.Remarks || followUp.remarks || '',
      Notes: followUp.Notes || followUp.notes,
      notes: followUp.Notes || followUp.notes
    });

    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {

    // Validate required fields
    if (!statusFormData.status || !statusFormData.statusId) {
      toast.error('Please select a status');
      return;
    }
    if (!selectedFollowUpForStatus) {
      toast.error('No follow-up selected');
      return;
    }

    try {
      // Prepare status data for individual update (use ID not name)
      const statusData = {
        status: statusFormData.status,
        statusId: statusFormData.statusId,
        outcomeId: statusFormData.outcomeId || 0,
        eventTypeId: statusFormData.eventTypeId,
        remarks: statusFormData.remarks || "",
        // Use AssignedToId (the GUID) instead of AssignedTo (the name)
        assignedTo: selectedFollowUpForStatus.assignedTo || selectedFollowUpForStatus.AssignedToId || selectedFollowUpForStatus.assignedToId,
        priority: selectedFollowUpForStatus.Priority || selectedFollowUpForStatus.priority || "medium",
        newScheduledDate: selectedFollowUpForStatus.ScheduledDate || selectedFollowUpForStatus.date
          ? new Date(selectedFollowUpForStatus.ScheduledDate || selectedFollowUpForStatus.date).toISOString()
          : new Date().toISOString(),
        newScheduledTime: selectedFollowUpForStatus.ScheduledTime || selectedFollowUpForStatus.scheduledTime || "09:00"
      };

      // Update status via hook (auto-invalidates SWR calendar & lead cache)
      await followUpActions.updateStatus(selectedFollowUpForStatus.FollowUpId || selectedFollowUpForStatus.id, statusData);

      // Success toast is shown in the service function
      setShowStatusModal(false);
      setSelectedFollowUpForStatus(null);

      // Refresh lead data to update upcoming follow-ups
      await fetchLeadData();
      invalidateTimelineCache(leadId);
    } catch (error) {
      console.error('Error updating follow-up status:', error);
      // Error toast is shown in the service function
    }
  };

  const handleAddFollowUp = (type = null) => {
    setEditingFollowUp(null);
    setSelectedActivityType(type);
    setShowFollowUpModal(true);
  };

  // Refresh only progress journey data
  const refreshProgressJourney = async () => {
    try {
      const response = await getLeadJourneyIndicators(leadId);
      if (response.success && response.data) {
        setProgressData(response.data);
      } else {
        console.error('Failed to refresh progress journey:', response.error);
      }
    } catch (err) {
      console.error('Error refreshing progress journey:', err);
    }
  };
  const handlePriorityChange = async (priority) => {
    try {
      setShowPriorityModal(false);

      const priorityValue = typeof priority === 'object' ? (priority.Priority || priority.priority) : priority;
      setLocalPriority(priorityValue);

      const updateObj = typeof priority === 'object'
        ? priority
        : { priority: priority, Priority: priority };
      setLeadData(prev => ({ ...prev, ...updateObj }));
      toast.success("Priority updated successfully");
      await fetchLeadData(priority);
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error("Failed to update priority. Please try again.");
    }
  };
  const handleInteractionLevelChange = async (interactionLevel) => {
    try {
      setShowInteractionLevelModal(false);

      const levelName = typeof interactionLevel === 'object' ? (interactionLevel.InteractionLevelName || interactionLevel.LeadStatus) : interactionLevel;
      setLocalInteractionLevel(levelName);

      const updateObj = typeof interactionLevel === 'object' ? interactionLevel : { InteractionLevelName: interactionLevel, LeadStatus: interactionLevel };
      setLeadData(prev => ({ ...prev, ...updateObj }));
      toast.success("Interaction updated successfully");
      await fetchLeadData(interactionLevel);
    } catch (error) {
      console.error("Error updating interaction level:", error);
      toast.error("Failed to update interaction level. Please try again.");
    }
  };

  const handleStepClick = (stepId) => {
    // Don't allow clicking on offer letter step if it's filtered out for Indian leads
    if (leadData?.country?.toLowerCase() === 'india' && stepId === 3) {
      return;
    }

    switch (stepId) {
      case 1:
        // Application Details -> No longer in right panel, maybe go to documents?
        setSelectedTab("documents");
        setSelectedContentTab("payment-details"); // Safe default
        break;
      case 2:
        // Document Upload -> Documents tab (reset content tab)
        setSelectedTab("documents");
        setSelectedContentTab("payment-details"); // Reset to default
        break;
      case 3:
        // Offer Letter Generated -> Navigate to offer letter tab
        setSelectedTab("offer-letter");
        break;
      case 4:
        // Payment Link -> Payment Details tab
        setSelectedTab("payment-details");
        break;
      case 5:
        // Payment Successful -> Payment Details tab
        setSelectedTab("payment-details");
        break;
      case 6:
        // Application Initiated -> Application Form tab (if promoted, otherwise maybe management)
        setSelectedTab("lead-management");
        break;
      case 7:
        // Application Submitted -> Application Form tab
        setSelectedTab("lead-management");
        break;
      case 8:
        // Interview Scheduled -> Google Meet tab
        setSelectedTab("lead-management");
        break;
      case 9:
        // Fee Payment Link -> Fee Payment tab
        setSelectedTab("payment-details");
        break;
      case 10:
        // Fee Payment -> Fee Payment tab
        setSelectedTab("payment-details");
        break;
      default:
        setSelectedTab("lead-management");
    }


    // Auto-scroll to content section
    if (contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };
  const renderTabContent = () => {
    return (
      <div className="flex-1 relative">
        {/* Lead Management Tab */}
        {visitedTabs.has("lead-management") && (
          <div id="section-lead-management" className={`animate-in fade-in duration-500 ${selectedTab !== "lead-management" ? "hidden" : ""}`}>
            <LeadManagementTab
              isMobile={isMobile}
              leadData={leadData}
              progressData={progressData}
              isReadOnly={isReadOnly}
              onAssignOwner={canAssignLead() ? () => setShowOwnerModal(true) : undefined}
              onEditFollowUp={handleEditFollowUp}
              onUpdateStatus={handleUpdateFollowUpStatus}
              onAddFollowUp={handleAddFollowUp}
              onChangeStage={canEditLead() ? () => setShowStageModal(true) : undefined}
              onSetPriority={canEditLead() ? () => setShowPriorityModal(true) : undefined}
              onSetInteractionLevel={canEditLead() ? () => setShowInteractionLevelModal(true) : undefined}
              onOpenNotesModal={() => {
                setSelectedTab("notes");
                setInternalOpenNotesAddModal(true);
              }}
              onAssignAcademic={canAssignLead() ? () => setShowAcademicModal(true) : undefined}
              onAssignAccountant={canAssignLead() ? () => setShowAccountantModal(true) : undefined}
              handleStepClick={handleStepClick}
              setProgressData={setProgressData}
            />
          </div>
        )}

        {/* Timeline Tab */}
        {visitedTabs.has("timeline") && (
          <div id="section-timeline" className={`h-full ${selectedTab !== "timeline" ? "hidden" : ""}`}>
            <TimelineView
              leadId={leadId}
              entityName={finalEntityName}
              scrollTargetId="details-content-scroll-container"
              refreshTrigger={refreshTrigger}
              isActive={selectedTab === "timeline"}
            />
          </div>
        )}


        {/* Follow-up Calendar Tab */}
        {visitedTabs.has("follow-up-calendar") && (
          <div id="section-follow-up-calendar" className={selectedTab === "follow-up-calendar" ? "" : "hidden"}>
            <CalendarTab
              isMobile={isMobile}
              leadData={leadData}
              initialDate={new URLSearchParams(window.location.search).get('date')}
              isStudent={leadData?.IsStudent}
              onOwnerChange={handleOwnerChange}
            />
          </div>
        )}

        {/* Notes Tab */}
        {visitedTabs.has("notes") && (
          <div id="section-notes" className={`h-full ${selectedTab !== "notes" ? "hidden" : ""}`}>
            <NotesTab
              isMobile={isMobile}
              leadData={leadData}
              openAddModalExternal={openNotesAddModal || internalOpenNotesAddModal}
              setOpenAddModalExternal={(val) => {
                setInternalOpenNotesAddModal(val);
                if (setOpenNotesAddModal) setOpenNotesAddModal(val);
              }}
              onRefreshParent={() => {
                setRefreshTrigger((prev) => prev + 1);
                invalidateTimelineCache(leadId);
                if (onLeadUpdate) onLeadUpdate(leadId, 'update');
              }}
              scrollTargetId="details-content-scroll-container"
            />
          </div>
        )}

        {/* Client Notes Tab */}
        {visitedTabs.has("client-notes") && (
          <div id="section-client-notes" className={`h-full ${selectedTab !== "client-notes" ? "hidden" : ""}`}>
            <ClientNotesTab
              type="lead"
              leadId={leadId}
              isMobile={isMobile}
              extraFieldsRequired={false}
              onRefreshParent={() => {
                setRefreshTrigger((prev) => prev + 1);
                invalidateTimelineCache(leadId);
              }}
            />
          </div>
        )}

        {/* Communications Tab */}
        {visitedTabs.has("communications") && (
          <div id="section-communications" className={selectedTab !== "communications" ? "hidden" : ""}>
            <CommunicationsTab leadId={leadId} isMobile={isMobile} initialChannelId={initialSubTab} />
          </div>
        )}

        {/* Documents Tab */}
        {visitedTabs.has("documents") && (
          <div id="section-documents" className={`h-full ${selectedTab !== "documents" ? "hidden" : ""}`}>
            <DocumentsTab
              leadId={leadId}
              isMobile={isMobile}
              isStudent={leadData?.IsStudent}
              leadData={leadData}
              useDocumentsData={useDocumentsData}
              refreshProgressJourney={async () => {
                setRefreshTrigger((prev) => prev + 1);
                invalidateTimelineCache(leadId);
                await refreshProgressJourney();
              }}
            />
          </div>
        )}

        {/* Offer Letter Tab */}
        {visitedTabs.has("offer-letter") && (
          <div id="section-offer-letter" className={selectedTab === "offer-letter" ? "" : "hidden"}>
            <OfferLetterTab leadId={leadId} leadData={leadData} isMobile={isMobile} onOfferLetterGenerated={fetchLeadData} />
          </div>
        )}

        {/* Contacts Tab */}
        {visitedTabs.has("contacts") && (
          <div id="section-contacts" className={selectedTab !== "contacts" ? "hidden" : ""} >
            <ContactsTab
              leadId={leadId}
              leadData={leadData}
              onRefreshParent={() => {
                setRefreshTrigger((prev) => prev + 1);
                invalidateTimelineCache(leadId);
                handleRefreshLead();
              }}
            />
          </div>
        )}
      </div>
    );
  };
  // Permission check (skip in modal mode - parent handles permissions)
  if (!isModal && !canViewLeadDetails()) {
    return (
      <Error401 />
    )
  }

  // Don't render content until data is loaded (UI handled by global loader)
  if (loading || !leadData) {
    if (isModal) {
      return (
        <div className="flex h-64 items-center justify-center">
          <div className="text-center">
            <div className="border-primary-600 mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"></div>
            <p className="dark:text-dark-400 text-gray-500">Loading lead details...</p>
          </div>
        </div>
      );
    }
    return null;
  }

  // Show error state
  if (error && !leadData) {
    const errorContent = (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <h3 className="dark:text-dark-100 mb-2 text-lg font-medium text-gray-900">
            Error Loading Lead
          </h3>
          <p className="dark:text-dark-400 mb-4 text-gray-500">
            {typeof error === "string" ? error : error?.message || "Failed to load lead details"}
          </p>
          <Button
            onClick={() => isModal ? fetchLeadData() : window.location.reload()}
            variant="outlined"
            color="primary"
          >
            Retry
          </Button>
        </div>
      </div>
    );

    if (isModal) {
      return errorContent;
    }

    return (
      <Page
        title={`${finalEntityName} Details`}
        component="div"
        className="dark:bg-dark-800 min-h-screen bg-gray-50"
      >
        {errorContent}
      </Page>
    );
  }

  // Main content - used in both modal and page mode
  const mainContent = (
    <>
      {/* Error banner if API failed but fallback data is shown */}
      {error && leadData && (
        <div className="mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:bg-yellow-900/20">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                <strong>Warning:</strong> Using fallback data. API Error:{" "}
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex ${isModal ? "flex-1" : "h-screen"} overflow-hidden bg-white dark:bg-dark-900 shadow-2xl rounded-xl border border-gray-200 dark:border-dark-800`}
      >
        {/* Left Part: Lead Summary & Details (from Drawer) */}
        {!isMobile && (
          <div className="hidden md:flex flex-col w-[360px] lg:w-[400px] shrink-0 border-r border-gray-200 dark:border-dark-600 h-full overflow-hidden">
            <LeadRightDrawerContent
              user={leadData}
              hideTabs={true}
              onClose={() => { }} // Not needed in inline mode
              onViewDetails={() => { }} // Already on details page
              onRefresh={() => fetchLeadData(true)}
              entityName={finalEntityName}
              onViewContactsTab={() => setSelectedTab("contacts")}
              useDocumentsData={useDocumentsData}
            />
          </div>
        )}

        {/* Right Part: Operations (Tabs & Content) */}
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden bg-[#f8fafc]/50 dark:bg-dark-900/40">
          {/* Top Navigation Bar */}
          <SidebarNavigation
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            isMobile={isMobile}
            showMobileSidebar={showMobileSidebar}
            setShowMobileSidebar={setShowMobileSidebar}
            selectedContentTab={selectedContentTab}
            setSelectedContentTab={setSelectedContentTab}
            entityName={finalEntityName}
            leadData={leadData}
            layout="horizontal"
            documentsCount={documentsCount}
          />

          <main className="flex-1 overflow-hidden pt-4 pb-0 bg-white dark:bg-dark-900 flex flex-col">
            <div id="details-content-scroll-container" className="flex-1 overflow-y-auto custom-scrollbar px-6 pb-12">
              {/* Show original header on mobile only since left panel is hidden */}
            

              {renderTabContent()}
            </div>
          </main>
        </div>
      </div>

      {/* Stage Change Confirmation Modal */}
      <ConfirmModal
        show={stageChangeConfirm.modalState.show}
        state={stageChangeConfirm.modalState.state}
        confirmLoading={stageChangeConfirm.modalState.loading}
        onClose={stageChangeConfirm.closeModal}
        onOk={stageChangeConfirm.handleConfirm}
        messages={stageChangeConfirm.messages}
        buttonColor="primary"
      />
      <StageChangeModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        leadData={leadData}
        onStageChange={handleStageChange}
      />

      {/* Action Modals */}
      <AssignOwnerModal
        isOpen={showOwnerModal}
        onClose={() => setShowOwnerModal(false)}
        leadData={leadData}
        currentOwnerName={leadData?.assignedOwner !== "N/A" ? leadData?.assignedOwner : null}
        onOwnerChange={handleOwnerChange}
      />

      <SetPriorityModal
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        leadData={leadData}
        handlePriorityChange={handlePriorityChange}
      />
      <SetInteractionLevelModal
        isOpen={showInteractionLevelModal}
        onClose={() => setShowInteractionLevelModal(false)}
        leadData={leadData}
        handleInteractionLevelChange={handleInteractionLevelChange}
      />

      <LeadAnalyticsModal
        isOpen={showAnalyticsModal}
        onClose={() => setShowAnalyticsModal(false)}
        leadData={leadData}
      />

      <WhatsAppMessageModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        leadData={leadData}
      />

      <EmailMessageModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        leadData={leadData}
      />

      <SMSMessageModal
        isOpen={showSMSModal}
        onClose={() => setShowSMSModal(false)}
        leadData={leadData}
      />

      <FollowUpScheduleModal
        key={editingFollowUp?.id || editingFollowUp?.FollowUpId || 'new-follow-up'}
        isOpen={showFollowUpModal}
        onClose={() => {
          setShowFollowUpModal(false);
          setEditingFollowUp(null);
          setSelectedActivityType(null);
        }}
        leadData={leadData}
        editingFollowUp={editingFollowUp}
        preSelectedActivityType={selectedActivityType}
        onSuccess={() => {
          setShowFollowUpModal(false);
          setEditingFollowUp(null);
          setSelectedActivityType(null);
          fetchLeadData(true);
          invalidateTimelineCache(leadId);
        }}
        onOwnerChange={handleOwnerChange}
        restrictedEditMode={false}
      />

      {/* Status Update Modal - Same as FollowUpsCalendar */}
      <Transition appear show={showStatusModal} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative w-full max-w-2xl max-h-[90vh] transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all dark:bg-dark-800 flex flex-col border border-gray-200 dark:border-dark-600">
              {/* Modern Header */}
              <div className="px-6 py-5 border-b border-gray-200 dark:border-dark-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                      <PencilIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <DialogTitle as="h3" className="text-xl font-semibold text-gray-900 dark:text-white">
                        Update Activity Status
                      </DialogTitle>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Change the status and outcome of this activity
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowStatusModal(false)}
                    variant="flat"
                    isIcon
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
                {/* Current Follow-up Info */}
                {selectedFollowUpForStatus && (
                  <Card className="p-4 border border-gray-200 dark:border-dark-600">
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-dark-700 rounded-full">
                        <UserIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {(() => {
                            const leadTitle = leadData?.LeadRawJsonData?.find((item) => item.MasterFieldName === "title")?.Value;
                            return leadTitle || leadData?.FullName || leadData?.name || leadData?.LeadName || "Lead";
                          })()}
                        </h4>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                          {(selectedFollowUpForStatus?.assignedToName || selectedFollowUpForStatus?.AssignedTo || selectedFollowUpForStatus?.UserName) && (
                            <div className="flex items-center space-x-1">
                              <UserIcon className="w-4 h-4" />
                              <span>{selectedFollowUpForStatus?.assignedToName || selectedFollowUpForStatus?.AssignedTo || selectedFollowUpForStatus?.UserName}</span>
                            </div>
                          )}
                          {(selectedFollowUpForStatus?.ScheduledTime || selectedFollowUpForStatus?.scheduledTime) && (
                            <div className="flex items-center space-x-1">
                              <ClockIcon className="w-4 h-4" />
                              <span>{(() => {
                                const timeStr = selectedFollowUpForStatus?.ScheduledTime;
                                if (!timeStr) return selectedFollowUpForStatus?.scheduledTime || "";
                                if (/AM|PM/i.test(timeStr)) return timeStr;
                                const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
                                if (match) {
                                  let hours = parseInt(match[1], 10);
                                  const minutes = match[2];
                                  const ampm = hours >= 12 ? "PM" : "AM";
                                  hours = hours % 12;
                                  hours = hours ? hours : 12;
                                  return `${hours}:${minutes} ${ampm}`;
                                }
                                return timeStr;
                              })()}</span>
                            </div>
                          )}
                          {selectedFollowUpForStatus?.StatusName && (
                            <Badge variant="soft" color="neutral">
                              {selectedFollowUpForStatus.StatusName}
                            </Badge>
                          )}
                        </div>

                        {/* Previous Remarks */}
                        {selectedFollowUpForStatus?.Remarks && (
                          <div className="mt-3 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-gray-500" />
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Previous Remarks</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                              {selectedFollowUpForStatus.Remarks}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Form Fields */}
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                  {/* Status Selection */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                      <div className="flex items-center space-x-2">
                        <CheckIcon className="w-4 h-4 text-primary-600" />
                        <span>Status</span>
                        <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <Select
                      value={statusFormData.statusId || ''}
                      onChange={(e) => {
                        const selectedStatus = statusOptions.find(s => s.id === parseInt(e.target.value));
                        setStatusFormData(prev => ({
                          ...prev,
                          status: selectedStatus?.name || '',
                          statusId: selectedStatus?.id || null
                        }));
                      }}
                      className="w-full"
                      placeholder="Choose status..."
                    >
                      <option value="">Choose status...</option>
                      {statusOptions.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                {/* Selected Event Info */}
                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                    <div className="flex items-center space-x-2">
                      <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-primary-600" />
                      <span>Remarks</span>
                      <span className="text-gray-400 text-xs">(Optional)</span>
                    </div>
                  </label>
                  <Textarea
                    value={statusFormData.remarks}
                    onChange={(e) => setStatusFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Add notes about this activity outcome..."
                    rows={4}
                    className="w-full resize-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-750">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Required fields marked with *</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => {
                        setShowStatusModal(false);
                        setStatusFormData({ status: '', statusId: null, outcome: '', outcomeId: null, eventTypeId: null, remarks: '' });
                      }}
                      variant="outlined"
                      color="neutral"
                      size="md"
                      className="min-w-[100px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleStatusUpdate}
                      variant="solid"
                      color="primary"
                      size="md"
                      disabled={!statusFormData.status}
                      className="min-w-[120px] shadow-lg"
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      Update Status
                    </Button>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      <CallModal
        isOpen={showCallModal}
        onClose={() => {
          setShowCallModal(false);
          // Note: The CallModal itself handles global widget state management
          // in its useEffect hooks, so no additional state management needed here
        }}
        leadData={leadData}
        onCallComplete={(callData) => {
          // You can add logic here to refresh call history or other components
          setShowCallModal(false);
        }}
      />

      <AssignAcademicCounsellorModal
        isOpen={showAcademicModal}
        onClose={() => setShowAcademicModal(false)}
        leadData={leadData}
        currentAcademicCounsellorName={
          leadData?.SourceDetails?.LeadAcademicCounsellorName ||
          leadData?.LeadAcademicCounsellorName ||
          leadData?.academicCounsellorName
        }
        onAcademicCounsellorChange={fetchLeadData}
      />

      <AssignAccountantCounsellorModal
        isOpen={showAccountantModal}
        onClose={() => setShowAccountantModal(false)}
        leadData={leadData}
        currentAccountantCounsellorName={
          leadData?.SourceDetails?.LeadAccountantCounsellorName ||
          leadData?.accountsCounsellorName ||
          leadData?.accountsCounsellor
        }
        onAccountantCounsellorChange={fetchLeadData}
      />

      <QuickLeadModal
        isOpen={showQuickLeadModal}
        onClose={() => {
          setShowQuickLeadModal(false);
          setSelectedFormForApplication(null);
          setIsComingFromPaymentFlow(false);
        }}
        leadId={leadId}
        leadData={leadData}
        categories={leadData?.categories}
        selectedForm={selectedFormForApplication}
        isApplicationForm={true}
        isComingFromPaymentFlow={isComingFromPaymentFlow}
        onLeadUpdated={() => {
          fetchLeadData(true);
          // If this is in a modal/drawer, close it fully after conversion to applicant
          if (onFullClose) onFullClose();
          else if (onClose) onClose();
        }}
      />

      <ConvertToJobModal
        show={showConvertToJobModal}
        onClose={() => setShowConvertToJobModal(false)}
        leadData={leadData}
        onReverted={() => {
          optimisticDataRef.current = {};
          setLocalStage(null);
          setLocalSubStage(null);
          invalidateTimelineCache(leadId);
          invalidateNotesCache(leadId);
          fetchLeadData(true);
          if (onLeadUpdate) onLeadUpdate(leadId, 'update');
        }}
      />

      <SetJobStatusModal
        show={showReactivateJobModal}
        onClose={handleCancelReactivate}
        jobId={reactivateJobId}
        currentStatusId={reactivateCurrentStatusId}
        excludeCancelled={true}
        onJobUpdated={() => {
          setPendingRevertLeadStageData(null);
          setShowReactivateJobModal(false);
          fetchLeadData(true);
        }}
      />

    </>
  );

  // In modal mode, return content directly without Page wrapper
  if (isModal) {
    return mainContent;
  }

  // In page mode, wrap with Page component
  return (
    <Page
      title="Lead Details"
      component="div"
      className={`dark:bg-dark-800 min-h-screen bg-gray-50 ${isMobile ? "mobile-container" : ""}`}
    >
      {mainContent}
    </Page>
  );
};

const LeadDetails = (props) => {
  const params = useParams();
  const leadId = props.leadIdProp || params.id;

  return (
    <LeadOptimisticStateProvider key={leadId}>
      <LeadDetailsInner {...props} />
    </LeadOptimisticStateProvider>
  );
};

export default LeadDetails;
